import { RichTranslator, useTranslator } from '@bluebeam/react-i18n';
import { List } from 'immutable';
import ImportConnectedAccounts from 'pages/DrawingsPage/PublishedTab/Imports/ImportConnectedAccounts';
import React, { FC, ReactElement, useEffect, useState } from 'react';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { ConnectedAccount } from 'records/ConnectedAccount';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { Modal, ModalContent, ModalFooter } from 'components/lib';
import { useIntegrationsCheck } from 'hooks/useIntegrationsCheck';

interface Props extends WithAppStoreProps, RouteComponentProps {
  onCancel: () => void;
}

const ImportDrawingsModal = ({
  onCancel,
  actions,
  appState,
  history,
}: Props): ReactElement => {
  const { translate } = useTranslator(['drawingsIntegrationsText', 'buttons']);
  const [connectedAccountsLoaded, setConnectedAccountsLoaded] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState<
    List<ConnectedAccount>
  >(List([]));
  const { areAccountIntegrationsEnabled } = useIntegrationsCheck();

  const getConnectedAccounts = async (): Promise<void> => {
    await actions.getConnectedAccounts();
    const newAppState = await actions.getConnectedAccounts(); // Avoids the race condition in AppState.

    const filteredConnectedAccounts = newAppState.connectedAccounts.filter(
      (connectedAccount) => !connectedAccount.isExpired
    );
    setConnectedAccounts(filteredConnectedAccounts);
    setConnectedAccountsLoaded(true);
  };

  useEffect(() => {
    getConnectedAccounts();
  }, []);

  const handleOnConnectionClick = async (
    providerType: string,
    connectedAccountId: string
  ) => {
    const projectId = appState.selectedProject;
    await actions.setIntegrationConnectedAccountId(connectedAccountId);
    history.push(`/project/${projectId}/integrations/import`);
    actions.clearAllModals();
  };

  const projectIntegrationsModal = (
    <Modal
      size="medium"
      onDismiss={onCancel}
      title={translate('drawingsIntegrationsText:modal.header')}
    >
      <div id="integrations-upload">
        <ModalContent>
          <div className="color-black-2 font-size-7 semi-bold left">
            {translate('drawingsIntegrationsText:modal.title')}
          </div>
          <div className="color-black-1 pt2 font-size-5 pb1 left">
            {translate('drawingsIntegrationsText:modal.description')}
          </div>

          <div className="column pt5">
            <div className="color-black-2 font-size-6 semi-bold pb2 left">
              {`${translate(
                'drawingsIntegrationsText:modal.connectedAccounts'
              )} (${connectedAccounts.size})`}
            </div>
            <div
              className="b-3 bgd-light-grey-2 column pb3 oxa left"
              style={{ height: 300 }}
            >
              {connectedAccountsLoaded ? (
                <ImportConnectedAccounts
                  accounts={connectedAccounts}
                  onClick={handleOnConnectionClick}
                />
              ) : (
                <div className="column h33 justify-center items-center">
                  <div className="row justify-between">
                    <div className="loading-animation-title">
                      {translate(
                        'drawingsIntegrationsText:modal.retrievingConnectedAccounts'
                      )}
                    </div>
                    <div className="loading-animation-ellipsis-wrap left">
                      <div className="loading-animation-ellipsis">...</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </ModalContent>

        <ModalFooter>
          <button
            id="integration-modal-cancel-btn"
            className="btn-lg btn-link mr2"
            onClick={onCancel}
          >
            {translate('buttons:cancel')}
          </button>
        </ModalFooter>
      </div>
    </Modal>
  );

  const accountIntegrationModal = (
    <Modal
      size="medium"
      onDismiss={onCancel}
      title={translate(
        'drawingsIntegrationsText:modal.accountIntegrations.header'
      )}
    >
      <div id="integrations-upload">
        <ModalContent>
          <div className="color-black-2 font-size-7 semi-bold left">
            {translate(
              'drawingsIntegrationsText:modal.accountIntegrations.title'
            )}
          </div>
          <div className="color-black-1 pt2 font-size-5 pb1 left">
            <RichTranslator
              components={[<Link to="/connected-accounts" target="_blank" />]}
              translationKey="drawingsIntegrationsText:modal.accountIntegrations.description"
            />
          </div>

          <div className="column pt5">
            <div className="color-black-2 font-size-6 semi-bold pb2 left">
              {`${translate(
                'drawingsIntegrationsText:modal.accountIntegrations.connectedAccounts'
              )} (${connectedAccounts.size})`}
            </div>
            <div
              className="b-3 bgd-light-grey-2 column pb3 oxa left"
              style={{ height: 300 }}
            >
              {connectedAccountsLoaded ? (
                <ImportConnectedAccounts
                  accounts={connectedAccounts}
                  onClick={handleOnConnectionClick}
                />
              ) : (
                <div className="column h33 justify-center items-center">
                  <div className="row justify-between">
                    <div className="loading-animation-title">
                      {translate(
                        'drawingsIntegrationsText:modal.accountIntegrations.retrievingConnectedAccounts'
                      )}
                    </div>
                    <div className="loading-animation-ellipsis-wrap left">
                      <div className="loading-animation-ellipsis">...</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </ModalContent>

        <ModalFooter>
          <button
            id="integration-modal-cancel-btn"
            className="btn-lg btn-link mr2"
            onClick={onCancel}
          >
            {translate('buttons:cancel')}
          </button>
        </ModalFooter>
      </div>
    </Modal>
  );

  return areAccountIntegrationsEnabled
    ? accountIntegrationModal
    : projectIntegrationsModal;
};

export default withRouter(withAppStore<Props>(ImportDrawingsModal));
